import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuthStore } from '../store/useAuthStore';

// Add type for window.meniooAuthStatus
declare global {
  interface Window {
    meniooAuthStatus: {
      authenticated: boolean;
      loading?: boolean;
      user?: any;
    };
    checkMeniooAuth?: () => Promise<boolean>;
  }
}

export function useInitializeApp() {
  const setAuthState = useAuthStore((state) => state.setAuthState);

  useEffect(() => {
    // Listen for auth status updates from auth-check.js
    const handleAuthStatusUpdate = (event: CustomEvent) => {
      if (event.detail?.authenticated) {
        setAuthState({
          currentUser: event.detail.user || {
            id: 'app-user',
            email: '',
            firstName: '',
            lastName: '',
            emailVerified: true,
            defaultOrganizationId: '',
            organizations: {}
          },
          currentOrganization: null,
          isAuthenticated: true,
          error: null,
          isLoading: false
        });
      }
    };

    // Add event listener for custom auth status update event
    window.addEventListener('menioo-auth-status-updated', handleAuthStatusUpdate as EventListener);

    // Initial check of window.meniooAuthStatus (may be set by auth-check.js already)
    if (window.meniooAuthStatus && window.meniooAuthStatus.authenticated) {
      setAuthState({
        currentUser: window.meniooAuthStatus.user || {
          id: 'app-user',
          email: '',
          firstName: '',
          lastName: '',
          emailVerified: true,
          defaultOrganizationId: '',
          organizations: {}
        },
        currentOrganization: null,
        isAuthenticated: true,
        error: null,
        isLoading: false
      });
    }

    // Check Firebase auth state
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // User is logged in with Firebase
          setAuthState({
            currentUser: {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              firstName: firebaseUser.displayName?.split(' ')[0] || '',
              lastName: firebaseUser.displayName?.split(' ')[1] || '',
              emailVerified: firebaseUser.emailVerified,
              defaultOrganizationId: '',
              organizations: {}
            },
            currentOrganization: null,
            isAuthenticated: true,
            error: null,
            isLoading: false
          });
        } else if (!window.meniooAuthStatus?.authenticated) {
          // If not authenticated in Firebase and not authenticated via app domain
          setAuthState({
            currentUser: null,
            currentOrganization: null,
            isAuthenticated: false,
            error: null,
            isLoading: false
          });
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        setAuthState({
          currentUser: null,
          currentOrganization: null,
          isAuthenticated: false,
          error: (error as Error).message,
          isLoading: false
        });
      }
    });

    return () => {
      window.removeEventListener('menioo-auth-status-updated', handleAuthStatusUpdate as EventListener);
      unsubscribe();
    };
  }, [setAuthState]);
}